/**
 * OperationalGate — Iter 10J route-level subscription gate.
 *
 * Wraps the authenticated AppShell. Whenever the SSOT
 * `tenant_status.is_operational === false` (trial_expired, suspended,
 * cancelled, deleted, grace_period-when-blocking), operational routes
 * are intercepted and a TrialExpiredLockScreen is rendered INSTEAD of
 * the tenant module. Modules therefore never mount their useEffect,
 * never call their APIs and cannot crash on `data.alerts` etc.
 *
 * Whitelist (routes that stay accessible even when blocked):
 *   • /billing*, /plans, /subscription, /checkout, /account, /profile
 *   • /settings/setup-guide, /settings/billing-documents,
 *     /settings/legal-documents, /settings/fiscal-profile
 *   • /help*, /support, /contact, /logout, /login, /signup
 *   • /platform* (owner-only routes, never gated for tenants either)
 *
 * Platform owners (`user.is_platform`) bypass the gate entirely.
 */
import React from "react";
import { useLocation } from "react-router-dom";
import { useAccessMatrix } from "@/context/AccessMatrixContext";
import { useAuth } from "@/context/AuthContext";
import TrialExpiredLockScreen from "@/components/subscription/TrialExpiredLockScreen";

// Route prefixes that must stay accessible even for locked tenants,
// so they can still pay, browse plans, download invoices, ask support.
const ALLOWED_PREFIXES = [
  "/billing",
  "/plans",
  "/subscription",
  "/checkout",
  "/account",
  "/profile",
  "/help",
  "/support",
  "/contact",
  "/logout",
  "/login",
  "/signup",
  "/platform",
  // Settings related to activation / paying / legal documents only:
  "/settings/setup-guide",
  "/settings/billing-documents",
  "/settings/legal-documents",
  "/settings/fiscal-profile",
];

function isAllowedRoute(pathname) {
  if (!pathname) return false;
  return ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p + "?")
  );
}

const BLOCKING_STATUSES = new Set([
  "trial_expired",
  "cancelled",
  "suspended",
  "deleted",
]);

export default function OperationalGate({ children }) {
  const location = useLocation();
  const { tenant_status, loading, isPlatform } = useAccessMatrix();
  const { logout } = useAuth();

  // Platform owner never gated.
  if (isPlatform) return children;
  // Wait for the matrix to load before deciding — flash a neutral shell.
  if (loading) return children;

  const status = tenant_status?.status;
  const isOperational = tenant_status?.is_operational !== false; // treat undefined as operational
  const isBlockingStatus = status && BLOCKING_STATUSES.has(status);

  // Only block operational routes. Billing / plans / support stay open.
  if (isBlockingStatus && !isOperational && !isAllowedRoute(location.pathname)) {
    return (
      <TrialExpiredLockScreen
        status={status}
        onLogout={logout}
        data-testid="operational-gate-lock"
      />
    );
  }

  return children;
}
